import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Car, LogOut, List, Settings as SettingsIcon, MessageSquare } from 'lucide-react';
import './index.css';
import logo from './images/logo.png';
import BASE_URL from './config';
import { useToast, ToastContainer } from './Toast';
import AlertModal from './AlertModal';
import SimilarRides from './SimilarRides';
import LOCATIONS from './locations';
import useUnreadCounts from './useUnreadCounts';

function NewRide() {
  const navigate = useNavigate();
  const [departureDate, setDepartureDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');

  // Toast and Alert modal state
  const { toasts, showToast, removeToast } = useToast();
  const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '', type: 'info' });
  const [fromLocation, setFromLocation] = useState('');
  const [fromCustom, setFromCustom] = useState('');
  const [toLocation, setToLocation] = useState('');
  const [toCustom, setToCustom] = useState('');
  const [notes, setNotes] = useState('');
  const [openSpots, setOpenSpots] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState('');
  const [minDate, setMinDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [similarRides, setSimilarRides] = useState([]);
  const [showSimilarModal, setShowSimilarModal] = useState(false);
  const [pendingRideData, setPendingRideData] = useState(null);

  const unreadCounts = useUnreadCounts();

  // Calculate today's date in the YYYY-MM-DD format
  useEffect(() => {
    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];
    setMinDate(formattedDate);
  }, []);

  // Functionality for submit button for form that allows users to create a new ride entry
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent double submission
    if (isSubmitting) {
      return;
    }

    // Validate all required fields
    if (!departureDate) {
      setAlertModal({
        isOpen: true,
        title: 'Date Required',
        message: 'Please select a departure date for your ride.',
        type: 'error'
      });
      return;
    }

    if (!startTime) {
      setAlertModal({
        isOpen: true,
        title: 'Earliest Departure Time Required',
        message: 'Please select an earliest departure time for your ride departure window.',
        type: 'error'
      });
      return;
    }

    if (!endTime) {
      setAlertModal({
        isOpen: true,
        title: 'Latest Departure Time Required',
        message: 'Please select a latest departure time for your ride departure window.',
        type: 'error'
      });
      return;
    }

    // Validate that end time is after start time
    if (startTime >= endTime) {
      setAlertModal({
        isOpen: true,
        title: 'Invalid Time Range',
        message: 'The latest departure time must be after the earliest departure time. Please select a valid time range.',
        type: 'error'
      });
      return;
    }

    // Resolve from/to locations (handle "Other" custom input)
    const resolvedFrom = fromLocation === '_other' ? `custom:${fromCustom.trim()}` : fromLocation;
    const resolvedTo = toLocation === '_other' ? `custom:${toCustom.trim()}` : toLocation;

    if (!resolvedFrom || (fromLocation === '_other' && !fromCustom.trim())) {
      setAlertModal({
        isOpen: true,
        title: 'Pickup Location Required',
        message: 'Please select where you are leaving from.',
        type: 'error'
      });
      return;
    }

    if (!resolvedTo || (toLocation === '_other' && !toCustom.trim())) {
      setAlertModal({
        isOpen: true,
        title: 'Destination Required',
        message: 'Please select where you are going to.',
        type: 'error'
      });
      return;
    }

    if (resolvedFrom === resolvedTo) {
      setAlertModal({
        isOpen: true,
        title: 'Same Location',
        message: 'Pickup and destination cannot be the same location.',
        type: 'error'
      });
      return;
    }

    // Validate date is in the future
    const selectedDate = new Date(departureDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
    
    if (selectedDate < today) {
      setAlertModal({
        isOpen: true,
        title: 'Invalid Date',
        message: 'The departure date must be today or later. Please select a future date.',
        type: 'error'
      });
      return;
    }

    // If date is today, validate that start time is in the future
    if (selectedDate.getTime() === today.getTime()) {
      const selectedStartDateTime = new Date(`${departureDate}T${startTime}`);
      const now = new Date();
      
      if (selectedStartDateTime <= now) {
        setAlertModal({
          isOpen: true,
          title: 'Invalid Earliest Departure Time',
          message: 'The earliest departure time must be in the future. Please select a later time.',
          type: 'error'
        });
        return;
      }
    }

    // Validate number of spots
    const spotsNum = parseInt(openSpots, 10);
    if (isNaN(spotsNum)) {
      setAlertModal({
        isOpen: true,
        title: 'Invalid Number of Spots',
        message: 'Please enter a valid number for open spots.',
        type: 'error'
      });
      return;
    }

    if (spotsNum < 1) {
      setAlertModal({
        isOpen: true,
        title: 'Invalid Number of Spots',
        message: 'The number of open spots must be at least 1.',
        type: 'error'
      });
      return;
    }

    if (spotsNum > 8) {
      setAlertModal({
        isOpen: true,
        title: 'Too Many Spots',
        message: 'The number of open spots cannot exceed 8 (typical car capacity).',
        type: 'error'
      });
      return;
    }

    const rideData = {
      departureDate,
      startTime,
      endTime,
      fromLocation: resolvedFrom,
      toLocation: resolvedTo,
      notes: notes.trim() || undefined,
      openSpots: spotsNum,
      username: user,
    };

    // Check for similar rides before creating
    setIsSubmitting(true);
    try {
      const matchResponse = await fetch(`${BASE_URL}/api/rides/match`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ departureDate, startTime, endTime, fromLocation: resolvedFrom, toLocation: resolvedTo }),
      });

      if (matchResponse.ok) {
        const matchData = await matchResponse.json();
        if (matchData.similar && matchData.similar.length > 0) {
          // Show similar rides modal — user can join one or proceed
          setSimilarRides(matchData.similar);
          setPendingRideData(rideData);
          setShowSimilarModal(true);
          setIsSubmitting(false);
          return;
        }
      }
    } catch (err) {
      // If match check fails, proceed with creation anyway
      console.log('Match check failed, proceeding with creation');
    }

    // No similar rides found — create directly
    await createRide(rideData);
  };

  const createRide = async (rideData) => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${BASE_URL}/newride`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(rideData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      // Clear form fields
      setDepartureDate('');
      setStartTime('');
      setEndTime('');
      setFromLocation('');
      setFromCustom('');
      setToLocation('');
      setToCustom('');
      setNotes('');
      setOpenSpots('');

      navigate('/rides', { state: { message: 'Ride created successfully!', type: 'success' } });
    } catch (error) {
      setAlertModal({
        isOpen: true,
        title: 'Submission Failed',
        message: `Failed to submit ride: ${error.message}`,
        type: 'error'
      });
      setIsSubmitting(false);
    }
  };

  const handleJoinSimilarRide = async (rideId) => {
    try {
      const response = await fetch(`${BASE_URL}/joinride`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ requestID: rideId, username: user }),
      });

      if (response.ok) {
        setShowSimilarModal(false);
        navigate('/rides', { state: { message: 'Joined existing ride!', type: 'success' } });
      } else {
        const err = await response.json();
        setAlertModal({
          isOpen: true,
          title: 'Could Not Join',
          message: err.error || 'Failed to join ride',
          type: 'error',
        });
      }
    } catch (error) {
      setAlertModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to join ride. Please try again.',
        type: 'error',
      });
    }
  };

  const handleProceedWithCreation = async () => {
    setShowSimilarModal(false);
    if (pendingRideData) {
      await createRide(pendingRideData);
    }
  };

  // Navigate to feed (main browse page)
  function goToHome() {
    navigate('/feed');
  }

  // Navigate to rides page for the Rides button as long as someone is signed in
  function goToRides() {
    if (isLoggedIn) {
      navigate('/rides');
    } else {
      setAlertModal({
        isOpen: true,
        title: 'Sign In Required',
        message: 'You need to sign in first to access the rides page.',
        type: 'warning'
      });
    }
  }

  // Allows users to log out as long as there is someone logged in that can be logged out
  function handleLogout() {
    if (isLoggedIn) {
      window.location.href = `${BASE_URL}/logout`;
      showToast('User logged out successfully!', 'success');
    } else {
      setAlertModal({
        isOpen: true,
        title: 'No User Signed In',
        message: 'There is no user currently signed in.',
        type: 'info'
      });
    }
  }

  // Checks to see if someone is logged in from the backend and gets their username
  useEffect(() => {
    // Check login status when the component mounts
    fetch(`${BASE_URL}/check_login`, {
      method: 'GET',
      credentials: 'include'
    })
    .then(response => response.json())
    .then(data => {
      if (data.isLoggedIn) {
        setIsLoggedIn(true);
        setUser(data.user)
      } else {
        setIsLoggedIn(false);
        // Redirect to home if not logged in
        navigate('/');
      }
    })
    .catch(error => {
      navigate('/');
    });
  }, [navigate]);

  return (
    <div>
      {/* Header on top that allows the user to navigate to different pages */}
      <header className="header">
        <div className="logo-container" onClick={goToHome}>
          <div className="logo-wrapper logo-light-mode">
            <img 
              src={logo} 
              alt="Logo" 
              className="logo"
            />
          </div>
          <h1>Bulldog Carpool</h1>
        </div>
        {/* Mobile Menu Toggle */}
        
        {/* Desktop Navigation */}
        <nav className="nav-buttons">
          <button
            className="nav-button"
            onClick={() => navigate('/feed')}
          >
            <List className="nav-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
            Feed
          </button>
          <button
            className="nav-button"
            onClick={() => navigate('/rides')}
          >
            <Car className="nav-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
            My Rides{unreadCounts.ride_join > 0 && <span className="nav-badge">{unreadCounts.ride_join}</span>}
          </button>
          <button className="nav-button" onClick={() => navigate('/messages')}>
            <MessageSquare className="nav-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
            Messages{unreadCounts.message > 0 && <span className="nav-badge">{unreadCounts.message}</span>}
          </button>
          <button className="nav-button" onClick={() => navigate('/settings')}>
            <SettingsIcon className="nav-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
            Settings
          </button>
          <button className="nav-button" onClick={handleLogout}>
            <LogOut className="nav-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
            Log Out
          </button>
        </nav>
        
      </header>

      {/* Form Page Content */}
      <div className="new-ride-page">
        <div className="new-ride-form-container">
          <div className="new-ride-form-header">
            <h2>Create a New Ride Request</h2>
          </div>
          <form onSubmit={handleSubmit} className="new-ride-form" noValidate>
            <div className="form-group">
              {/* Field on form that allows users to enter the date of departure for their ride */}
              <label htmlFor="departureDate">Date of Departure:</label>
              <input
                type="date"
                id="departureDate"
                value={departureDate}
                onChange={(e) => setDepartureDate(e.target.value)}
                min={minDate}
                placeholder="Select departure date"
              />
            </div>
            <div className="form-group">
              {/* Field on form that allows users to enter the start time of departure window */}
              <label htmlFor="startTime">Earliest Departure Time:</label>
              <input
                type="time"
                id="startTime"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                placeholder="Select earliest departure time"
              />
            </div>
            <div className="form-group">
              {/* Field on form that allows users to enter the end time of departure window */}
              <label htmlFor="endTime">Latest Departure Time:</label>
              <input
                type="time"
                id="endTime"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                placeholder="Select latest departure time"
              />
            </div>
            <div className="form-group">
              <label htmlFor="fromLocation">Leaving From:</label>
              <select
                id="fromLocation"
                value={fromLocation}
                onChange={(e) => setFromLocation(e.target.value)}
              >
                <option value="">Select pickup location</option>
                <optgroup label="Campus">
                  {LOCATIONS.filter(l => l.type === 'campus').map(l => (
                    <option key={l.key} value={l.key}>{l.label}</option>
                  ))}
                </optgroup>
                <optgroup label="Airports">
                  {LOCATIONS.filter(l => l.type === 'airport').map(l => (
                    <option key={l.key} value={l.key}>{l.label}</option>
                  ))}
                </optgroup>
                <option value="_other">Other...</option>
              </select>
              {fromLocation === '_other' && (
                <input
                  type="text"
                  value={fromCustom}
                  onChange={(e) => setFromCustom(e.target.value)}
                  placeholder="Enter pickup location"
                  className="custom-location-input"
                  maxLength={200}
                />
              )}
            </div>
            <div className="form-group">
              <label htmlFor="toLocation">Going To:</label>
              <select
                id="toLocation"
                value={toLocation}
                onChange={(e) => setToLocation(e.target.value)}
              >
                <option value="">Select destination</option>
                <optgroup label="Airports">
                  {LOCATIONS.filter(l => l.type === 'airport').map(l => (
                    <option key={l.key} value={l.key}>{l.label}</option>
                  ))}
                </optgroup>
                <optgroup label="Campus">
                  {LOCATIONS.filter(l => l.type === 'campus').map(l => (
                    <option key={l.key} value={l.key}>{l.label}</option>
                  ))}
                </optgroup>
                <option value="_other">Other...</option>
              </select>
              {toLocation === '_other' && (
                <input
                  type="text"
                  value={toCustom}
                  onChange={(e) => setToCustom(e.target.value)}
                  placeholder="Enter destination"
                  className="custom-location-input"
                  maxLength={200}
                />
              )}
            </div>
            <div className="form-group">
              <label htmlFor="openSpots">Open Spots:</label>
              <input
                type="number"
                id="openSpots"
                value={openSpots}
                onChange={(e) => setOpenSpots(e.target.value)}
                min="1"
                max="8"
                placeholder="Enter number of open spots (1-8)"
              />
            </div>
            <div className="form-group">
              <label htmlFor="notes">Notes (optional):</label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="E.g., I have 2 large bags, can leave earlier, Terminal B pickup..."
                maxLength={280}
                rows={3}
                className="ride-notes-input"
              />
              <small className="char-count">{notes.length}/280</small>
            </div>
            <button type="submit" className="submit-button" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Ride Request'}
            </button>
          </form>
        </div>
      </div>

      {/* Toast notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {/* Alert modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
      />

      {/* Similar rides modal */}
      <SimilarRides
        rides={similarRides}
        isOpen={showSimilarModal}
        onClose={() => { setShowSimilarModal(false); setIsSubmitting(false); }}
        onJoin={handleJoinSimilarRide}
        onProceed={handleProceedWithCreation}
      />
    </div>
  );
}

export default NewRide;
